import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Package, Truck, MapPin, LogOut } from "lucide-react";

const STATUS_LABELS = {
  received: "Received",
  preparing: "Preparing",
  ready_for_pickup: "Ready for Pickup",
  shipped: "Shipped",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  picked_up: "Picked Up",
  cancelled: "Cancelled",
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const PAY_BADGE = {
  paid: { label: "Paid", cls: "bg-[#2f6f3a]/15 text-[#2f6f3a] border-[#2f6f3a]/30" },
  pending: { label: "Payment Pending", cls: "bg-copper/15 text-copper border-copper/30" },
  unpaid: { label: "Pay at Pickup/Delivery", cls: "bg-white text-charcoal/70 border-tan/50" },
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
      <div className="bg-sand/90 backdrop-blur-sm border border-tan/50 p-8 lg:p-10 float-card">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <span className="eyebrow">My Account</span>
            <h1 className="font-serif text-4xl text-navy mt-3">Welcome, {user.name?.split(" ")[0]}.</h1>
            <p className="text-charcoal/75 mt-2">{user.email} · {user.role === "customer" ? "Customer" : user.role}</p>
            {user.role === "customer" && (
              <p className="text-[11px] mt-2 inline-block px-2 py-1 bg-white border border-tan/40 text-charcoal/80 uppercase tracking-[0.18em]">
                ID Verification: {user.id_verified ? "Verified" : "Pending review"}
              </p>
            )}
          </div>
          <button data-testid="account-logout" onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm border border-navy text-navy px-5 py-2.5 hover:bg-navy hover:text-white">
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl text-white">My Orders</h2>
        <div className="mt-4 space-y-4">
          {orders.length === 0 && (
            <div className="bg-sand/85 border border-tan/40 p-8 text-center">
              <p className="text-charcoal/80">No orders yet.</p>
              <Link to="/shop" className="mt-4 inline-block bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-charcoal">Browse the catalog</Link>
            </div>
          )}
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className="bg-sand/90 border border-tan/40 p-6 float-card">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <div className="font-serif text-xl text-navy">{o.order_number}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-charcoal/70 mt-1 flex items-center gap-2">
                    {o.fulfillment === "pickup" ? <><Package size={12}/> Pickup</> : <><Truck size={12}/> Shipping</>}
                    <span>·</span><span>{new Date(o.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <span className="bg-navy text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em]">{STATUS_LABELS[o.status] || o.status}</span>
              </div>
              <ul className="mt-4 text-sm text-charcoal/85 space-y-1">
                {o.items.map((it, i) => <li key={i}>· {it.qty}× {it.name} {it.price_display && <span className="text-charcoal/60">— {it.price_display}</span>}</li>)}
              </ul>
              <div className="mt-3 flex items-center justify-between flex-wrap gap-2 border-t border-tan/30 pt-3">
                <span data-testid={`order-payment-${o.order_number}`} className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border ${(PAY_BADGE[o.payment_status] || PAY_BADGE.unpaid).cls}`}>
                  {(PAY_BADGE[o.payment_status] || PAY_BADGE.unpaid).label}
                </span>
                {o.total > 0 && <span className="font-serif text-xl text-navy">{money(o.total)}</span>}
              </div>
              {o.tracking_number && (
                <div className="mt-3 text-sm text-navy">Tracking #: <span className="font-medium">{o.tracking_number}</span></div>
              )}
              {o.shipping_address && (
                <div className="mt-3 text-xs text-charcoal/70 flex items-start gap-2"><MapPin size={12} className="mt-0.5"/> {o.shipping_address.line1}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
